import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";
import { AuthenticationContext } from "../context";

export interface DrugConfiguration {
  readonly start_date: string;
  readonly end_date: string;
  readonly drug_name_id: number;
  readonly drug_descriptor_id: string;
  readonly ndc: string;
  readonly dosage_form: string;
  readonly dosage_form_packaging: string;
  readonly dosage: string;
  readonly days_supply_multiplier: number;
  readonly quantities: number[];
  readonly quantity_multiplier: number;
  readonly display_quantities: string[];
}

export interface Drug {
  readonly drug_name: string;
  readonly alternative_class_identifier: string;
  readonly alternative_class_identifier_type: string;
  readonly drug_type: "Brand" | "Generic";
  readonly id: number;
  readonly most_common_dosage?: string;
  readonly most_common_form?: string;
  readonly most_common_quantity_dispensed?: string;
  readonly configurations: DrugConfiguration[];
}

export interface DrugListRequest {
  readonly search?: string;
  readonly page_number?: number;
}

export interface DrugListResponse {
  readonly results: { readonly drug: Drug }[];
}

export const listDrugs = async (authToken: string, body: DrugListRequest) => {
  return request<DrugListRequest, DrugListResponse>({
    endpoint: "/drug/list",
    body: { ...(body || {}) },
  }, authToken);
};

export const useListDrugsQuery = (filters: DrugListRequest) => {
  const queryKey = ["drugList", filters.search];
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      return listDrugs(await getAuthToken(), filters);
    },
    queryKey: queryKey,
    enabled: filters?.search?.length >= 3,
    staleTime: 300,
    retry: false,
  });

  return query;
};

export interface DrugGetResponse {
  readonly alternatives: { readonly drug: Drug }[];
  readonly drug: Drug;
}

export const getDrug = async (authToken: string, drugId: number) => {
  return request<{ readonly drug_id: number }, DrugGetResponse>({
    endpoint: "/drug/get",
    body: { drug_id: drugId },
  }, authToken);
};

export const useGetDrugQuery = (drugId: number) => {
  const { getAuthToken } = useContext(AuthenticationContext);

  return useQuery<DrugGetResponse>({
    queryFn: async () => {
      return getDrug(await getAuthToken(), drugId);
    },
    queryKey: ["getDrug", drugId],
    retry: false,
    staleTime: 300,
    enabled: !!drugId,
  });
};

export interface SubmissionClarificationCode {
  program: string;
  submission_clarification_code: string;
}

export interface DrugPricesRequest {
  readonly drug_id: number;
  readonly dosage_form: string;
  readonly dosage: string;
  readonly quantity: number;
  readonly days_supply: number;
  readonly location: {
    readonly latitude: string;
    readonly longitude: string;
  };
  readonly override_codes?: string[];
  readonly page_number?: number;
  readonly bounding_box?: {
    readonly ne: string;
    readonly nw: string;
    readonly se: string;
    readonly sw: string;
  };
  readonly submission_clarification_code?: SubmissionClarificationCode;
}

export interface PricesClaim {
  readonly formulary?: {
    readonly [key: string]: {
      readonly affordable_care_act: string;
      readonly age_limit_original: string;
      readonly brand_for_generic: string;
      readonly brand_name: string;
      readonly brand_with_ab_rated_generic: string;
      readonly compound_kit: string;
      readonly created_at: string;
      readonly daw: string;
      readonly device_flag: string;
      readonly drug_class: string;
      readonly end_date: string;
      readonly exclusion_category: string;
      readonly extended_days_supply: string;
      readonly file_id: number;
      readonly gender: string;
      readonly generic_description: string;
      readonly generic_name: string;
      readonly generic_therapeutic_class_description: string;
      readonly gpi: string;
      readonly high_cost_brand: string;
      readonly high_cost_generic: string;
      readonly id: number;
      readonly injectable: string;
      readonly inner_pack_flag: string;
      readonly label_name: string;
      readonly low_cost_generic: string;
      readonly maintenance: string;
      readonly maintenance_flag: string;
      readonly maximum_age_limit: string;
      readonly minimum_age_limit: string;
      readonly mme_conversion_factor: string;
      readonly multi_source: string;
      readonly name: string;
      readonly narrow_therapeutic_indicator: string;
      readonly ndc: string;
      readonly opioid: string;
      readonly otc: string;
      readonly preferred: string;
      readonly preferred_specialty: string;
      readonly preventive: string;
      readonly prior_authorization: string;
      readonly quantity_limit: string;
      readonly repackage_flag: string;
      readonly route_of_administration: string;
      readonly rx_cap: string;
      readonly rx_cap_coinsurance: string;
      readonly rx_cap_plus: string;
      readonly rx_otc_indicator_code: string;
      readonly specialty: string;
      readonly split_fill: string;
      readonly start_date: string;
      readonly step_therapy: string;
      readonly strength_per_unit: string;
      readonly third_party_restriction_code: string;
      readonly tier: string;
      readonly tier_original: string;
      readonly type: string;
      readonly unbreakable: string;
    };
  };
  readonly request: {
    readonly claim: {
      readonly compound_code: string;
      readonly date_prescription_written: string;
      readonly daw: string;
      readonly days_supply: number;
      readonly fill_number: number;
      readonly ndc: string;
      readonly number_of_refills_authorized: number;
      readonly other_coverage_code: string;
      readonly prescription_service_reference_number: string;
      readonly prescription_service_reference_number_qualifier: string;
      readonly product_service_id_qualifier: string;
      readonly quantity_dispensed: number;
    };
  };
  readonly response: {
    readonly header?: {
      readonly date_of_service?: string;
      readonly header_response_status?: string;
      readonly service_provider_id?: string;
      readonly service_provider_id_qualifier?: string;
      readonly transaction_code?: string;
      readonly transaction_count?: string;
      readonly version_release_number?: string;
    };
    readonly response_claim?: {
      readonly benefit_type_opportunities?: { benefit_type_opportunity?: string }[];
      readonly formulary_alternative_products?: {
        readonly formulary_alternative_cost_share_incentive?: string;
        readonly formulary_alternative_description?: string;
        readonly formulary_alternative_id?: string;
        readonly formulary_alternative_id_qualifier?: string;
        readonly formulary_alternative_incentive?: string;
        readonly formulary_alternative_plan_benefit_tier?: string;
        readonly formulary_alternative_reason_code?: string;
        readonly formulary_alternative_required_therapy_indicators?: {
          readonly formulary_alternative_required_therapy_indicator?: string;
          readonly formulary_alternative_required_therapy_time_period_duration?: string;
          readonly formulary_alternative_required_therapy_time_period_end_date?: string;
          readonly formulary_alternative_required_therapy_time_period_qualifier?: string;
          readonly formulary_alternative_required_therapy_time_period_start_date?: string;
        }[];
      }[];
      readonly maximum_age?: string;
      readonly maximum_age_qualifier?: string;
      readonly maximum_amount?: string;
      readonly maximum_amount_qualifier?: string;
      readonly maximum_amount_time_period?: string;
      readonly maximum_amount_time_period_end_date?: string;
      readonly maximum_amount_time_period_start_date?: string;
      readonly maximum_amount_time_period_units?: string;
      readonly medicaid_subrogation_icn_tcn?: string;
      readonly minimum_age?: string;
      readonly minimum_age_qualifier?: string;
      readonly minimum_amount?: string;
      readonly minimum_amount_qualifier?: string;
      readonly plan_benefit_override_indicator?: string;
      readonly plan_benefit_override_values?: { plan_benefit_override_value?: string }[];
      readonly prescription_service_reference_number?: string;
      readonly prescription_service_reference_number_qualifier?: string;
      readonly remaining_amount?: string;
      readonly remaining_amount_qualifier?: string;
      readonly subrogation_requestors_reconciliation_id?: string;
    };
    readonly response_dur?: {
      readonly dur_pps_response_codes?: {
        readonly clinical_significance_code?: string;
        readonly database_indicator?: string;
        readonly dur_additional_text?: string;
        readonly dur_free_text_message?: string;
        readonly other_pharmacy_indicator?: string;
        readonly other_prescriber_indicator?: string;
        readonly previous_date_of_fill?: string;
        readonly quantity_of_previous_fill?: string;
        readonly reason_for_service_code?: string;
      }[];
    };
    readonly response_insurance?: {
      readonly cardholder_id?: string;
      readonly group_id?: string;
      readonly medicaid_agency_number?: string;
      readonly medicaid_id_number?: string;
      readonly network_reimbursement_id?: string;
      readonly payer_health_plan_id?: string;
      readonly payer_health_plan_id_qualifier?: string;
      readonly plan_id?: string;
    };
    readonly response_intermediary?: {
      readonly intermediary_help_desk_support_types?: {
        readonly intermediary_help_desk_business_unit_types: {
          readonly intermediary_help_desk_business_unit_type?: string;
          readonly intermediary_help_desk_contact_information?: string;
          readonly intermediary_help_desk_contact_information_extension?: string;
          readonly intermediary_help_desk_contact_information_qualifier?: string;
        }[];
        readonly intermediary_help_desk_support_type?: string;
      }[];
      readonly intermediary_message?: string;
      readonly response_intermediary_authorization_id?: string;
      readonly response_intermediary_authorizations?: { response_intermediary_authorization_type_id?: string }[];
    };
    readonly response_message?: { readonly message?: string };
    readonly response_other_payers?: {
      readonly other_payer_ids?: {
        readonly other_payer_adjudicated_program_type?: string;
        readonly other_payer_benefit_classification?: string;
        readonly other_payer_benefit_effective_date?: string;
        readonly other_payer_benefit_termination_date?: string;
        readonly other_payer_cardholder_id?: string;
        readonly other_payer_coverage_type?: string;
        readonly other_payer_group_id?: string;
        readonly other_payer_help_desk_telephone_number?: string;
        readonly other_payer_help_desk_telephone_number_extension?: string;
        readonly other_payer_id?: string;
        readonly other_payer_id_qualifier?: string;
        readonly other_payer_name?: string;
        readonly other_payer_patient_relationship_code?: string;
        readonly other_payer_person_code?: string;
        readonly other_payer_processor_control_number?: string;
        readonly other_payer_relationship_type?: string;
      }[];
    };
    readonly response_patient?: {
      readonly date_of_birth?: string;
      readonly patient_first_name?: string;
      readonly patient_last_name?: string;
    };
    readonly response_pricing?: {
      readonly accumulated_deductible_amount?: string;
      readonly amount_applied_to_periodic_deductible?: string;
      readonly amount_attributed_to_coverage_gap?: string;
      readonly amount_attributed_to_processor_fee?: string;
      readonly amount_attributed_to_product_selection_brand_drug?: string;
      readonly amount_attributed_to_product_selection_brand_non_preferred_formulary_selection?: string;
      readonly amount_attributed_to_product_selection_non_preferred_formulary_selection?: string;
      readonly amount_attributed_to_provider_network_selection?: string;
      readonly amount_attributed_to_sales_tax?: string;
      readonly amount_exceeding_periodic_benefit_maximum?: string;
      readonly amount_of_coinsurance?: string;
      readonly amount_of_copay?: string;
      readonly basis_of_calculation_coinsurance?: string;
      readonly basis_of_calculation_copay?: string;
      readonly basis_of_calculation_dispensing_fee?: string;
      readonly basis_of_calculation_percentage_tax?: string;
      readonly basis_of_calculation_regulatory_fee?: string;
      readonly basis_of_reimbursement_determination?: string;
      readonly benefit_stage_amounts?: {
        readonly benefit_stage_amount?: string;
        readonly benefit_stage_qualifier?: string;
      }[];
      readonly dispensing_fee_contracted_reimbursable_amount?: string;
      readonly dispensing_fee_paid?: string;
      readonly estimated_generic_savings?: string;
      readonly health_plan_funded_assistance_amount?: string;
      readonly incentive_amount_paid?: string;
      readonly ingredient_cost_contracted_reimbursable_amount?: string;
      readonly ingredient_cost_paid?: string;
      readonly other_amounts_paid?: {
        readonly other_amount_paid?: string;
        readonly other_amount_paid_qualifier?: string;
      }[];
      readonly other_payer_amount_recognized?: string;
      readonly patient_pay_amount?: string;
      readonly patient_pay_components?: {
        readonly patient_pay_component_amount?: string;
        readonly patient_pay_component_qualifier?: string;
      }[];
      readonly patient_percentage_tax_amount?: string;
      readonly patient_regulatory_fee_amount?: string;
      readonly percentage_tax_amount_paid?: string;
      readonly percentage_tax_basis_paid?: string;
      readonly percentage_tax_exempt_indicator?: string;
      readonly percentage_tax_rate_paid?: string;
      readonly plan_sales_tax_amount?: string;
      readonly regulatory_fee_amount_paid?: string;
      readonly regulatory_fee_exempt_indicator?: string;
      readonly regulatory_fees?: { regulatory_fee_type_code?: string }[];
      readonly remaining_benefit_amount?: string;
      readonly remaining_deductible_amount?: string;
      readonly spending_account_amount_remaining?: string;
      readonly total_amount_paid?: string;
    };
    readonly response_prior_authorization?: {
      readonly prior_authorization_expiration_date?: string;
      readonly prior_authorization_number_assigned?: string;
    };
    readonly response_status?: {
      readonly additional_messages?: {
        readonly additional_message_information?: string;
        readonly additional_message_information_continuity?: string;
        readonly additional_message_information_qualifier?: string;
      }[];
      readonly adjudicated_program_type?: string;
      readonly approved_message_codes?: { readonly approved_message_code?: string }[];
      readonly authorization_number?: string;
      readonly help_desk_phone_number?: string;
      readonly help_desk_phone_number_qualifier?: string;
      readonly help_desk_support_types?: {
        readonly help_desk_business_unit_types?: {
          readonly help_desk_business_unit_type?: string;
          readonly help_desk_contact_information?: string;
          readonly help_desk_contact_information_extension?: string;
          readonly help_desk_contact_information_qualifier?: string;
        }[];
        readonly help_desk_support_type?: string;
      }[];
      readonly internal_control_number?: string;
      readonly next_available_fill_date?: string;
      readonly reconciliation_id?: string;
      readonly reject_codes?: {
        readonly reject_code?: string;
        readonly reject_field_occurrence_indicator?: string;
      }[];
      readonly reject_message?: string;
      readonly transaction_response_status?: string;
      readonly url?: string;
    };
  };
  readonly transmission: {
    readonly claim_status?: "R" | "P";
    readonly id?: number;
  };
  readonly programs: string[];
}

export interface PricesPharmacy {
  readonly address_line_1: string;
  readonly address_line_2?: string;
  readonly bounding_box?: string;
  readonly city: string;
  readonly delivery_network_redirect_url?: string;
  readonly hours: string[];
  readonly is_24_hours: string;
  readonly languages: string[];
  readonly latitude: string;
  readonly longitude: string;
  readonly minority_owned_pharmacy: string;
  readonly name: string;
  readonly network_status?: string;
  readonly npi?: string;
  readonly phone: string;
  readonly provider_id: string;
  readonly distance?: number;
  readonly state: string;
  readonly zip: string;
  readonly services: {
    readonly "24 Hour Emergency Service": string;
    readonly "340b Status": string;
    readonly "Closed Door Facility": string;
    readonly "Compounding Service": string;
    readonly "Delivery Service": string;
    readonly "Drive-thru": string;
    readonly "Durable Medical Equipment": string;
    readonly "E-Prescriptions": string;
    readonly "Flu Shots": string;
    readonly "Handicap Accessible": string;
    readonly "Multi Dose Compliance Packaging": string;
    readonly "Walk In Clinic": string;
  };
  readonly pharmacy_lookup_rule_id?: number;
  readonly pharmacy_display_rules?: {
    readonly display_tag?: string;
    readonly description?: string;
    readonly display_on_map?: "check" | "never";
  };
}

export interface Prices {
  readonly drug: Drug;
  readonly pharmacy: PricesPharmacy;
  readonly claim: PricesClaim;
  readonly is_rx_for_less: boolean;
}

export interface PricesRequestConfiguration {
  readonly drug_id: number;
  readonly dosage: string;
  readonly dosage_form: string;
  readonly location: {
    readonly latitude: string;
    readonly longitude: string;
  };
  readonly override_codes?: string[];
  readonly quantity: number;
}

export interface PricesRejectInfo {
  readonly reject_code: string;
  readonly reject_message: string;
  readonly rejected_by_all_pharmacies: boolean;
  readonly reject_message_list: string[];
}

export interface DrugPrices {
  readonly prices: Prices[];
  readonly generic_prices?: Prices[];
  readonly request_configurations: PricesRequestConfiguration;
  readonly reject_info?: PricesRejectInfo;
  readonly selected_mail_provider_id?: string;
  readonly selected_in_house_provider_id?: string;
  readonly selected_specialty_provider_id?: string;
  readonly selected_mail_provider_ids?: string[];
  readonly selected_in_house_provider_ids?: string[];
  readonly selected_specialty_provider_ids?: string[];
  readonly custom_member_messages?: string[];
  readonly applied_overrides?: string[];
  readonly using_pharmacy_lookup_rules?: boolean;
  readonly result_count?: string;
}

export const listDrugPrices = async (authToken: string, body: DrugPricesRequest & Partial<ViewRequest>) => {
  return request<typeof body, DrugPrices>({
    endpoint: "/drug/prices/list",
    body: { ...body },
  }, authToken);
};

export const useListDrugPricesQuery = (filters: DrugPricesRequest, enabled = true) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      const authToken = await getAuthToken();
      if (isViewingDataholder) {
        return listDrugPrices(authToken, {
          ...filters,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listDrugPrices(authToken, filters);
      }
    },
    queryKey: [
      "drugPriceList",
      filters.page_number,
      filters.drug_id,
      filters.dosage,
      filters.dosage_form,
      filters.quantity,
      filters.location.latitude,
      filters.location.longitude,
      ...(filters.override_codes || []),
      filters.submission_clarification_code?.program,
      filters.submission_clarification_code?.submission_clarification_code,
      ...getEligibilityBasedQueryKey(memberEligibility),
    ],
    enabled: enabled,
    staleTime: Infinity,
  });

  return query;
};

export interface DMRDrug {
  readonly ndc: string;
  readonly drug_name: string;
  readonly dosage?: string;
  readonly dosage_form?: string;
  readonly gpi?: string;
  readonly package_size?: string;
  readonly package_size_unit_of_measure?: string;
  readonly repackaged_code?: string;
}

export interface DMRDrugListResponse {
  readonly results: { readonly drug: DMRDrug }[];
}

export interface ListDMRDrugsRequest {
  readonly ndc?: string;
}

export const listDMRDrugs = async (
  authToken: string,
  body: ListDMRDrugsRequest & Partial<ViewRequest>,
) => {
  return request<typeof body, DMRDrugListResponse>({
    endpoint: "/dmr/drug/list",
    body: { ...body },
  }, authToken);
};

export const useListDMRDrugsQuery = (
  params: ListDMRDrugsRequest,
  enabled: boolean = false,
) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["drugDMRList"];

  if (params?.ndc) {
    queryKey.push(params.ndc);
  }

  const query = useQuery({
    queryFn: async () => {
      return listDMRDrugs(await getAuthToken(), params);
    },
    queryKey: queryKey,
    enabled: enabled,
    retry: false,
  });

  return query;
};
